import { useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import { selectCurrentBranch } from "../../../../core/data/redux/slice/branchesSlice"
import { selectCurrentSession } from "../../../../core/data/redux/slice/sessionsSlice"
import { absoluteCampusLogoUrl, campusBrandName } from "../../../../utils/campusLogos"
import {
  listTransferCertificates,
  findStudentForTC,
  issueTransferCertificate,
  updateTransferCertificate,
  cancelTransferCertificate,
  type TransferCertificate,
  type TCStudent,
} from "../../../../service/transfer-certificate"
import { printTransferCertificate } from "./tcPrint"
import CollectFeesModal from "../../../management/feescollection/collectFeesModal"
import { TUITION_FEE_GROUP } from "../../../../constant"

const REASONS = [
  "On Parent Request",
  "Transfer to another campus",
  "Shifted to another city",
  "Financial reasons",
  "Completed final class",
  "Other",
]
const GRADES = ["Excellent", "Very Good", "Good", "Satisfactory", "Fair", "Poor", "Other"]
const PROGRESS_STATES = ["In Progress", "Passed", "Failed", "Not Applicable"]

const fmt = (d?: string | null) => (d ? new Date(d).toLocaleDateString("en-GB") : "—")
const asInput = (d?: string | null) => (d ? new Date(d).toISOString().slice(0, 10) : "")

const TransferCertificatePage = () => {
  const [searchParams] = useSearchParams()
  const currentBranch = useSelector(selectCurrentBranch)
  const currentSession = useSelector(selectCurrentSession)

  const [rows, setRows] = useState<TransferCertificate[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState("")

  // Issue / edit panel
  const [gr, setGr] = useState("")
  const [looking, setLooking] = useState(false)
  const [student, setStudent] = useState<TCStudent | null>(null)
  const [editing, setEditing] = useState<TransferCertificate | null>(null)
  const [form, setForm] = useState<Record<string, any>>({})
  const [saving, setSaving] = useState(false)
  // The student handed to the fee modal. Same modal Fees Collection and the
  // Defaulters list use, so the payment and its receipt go through exactly the
  // one flow the cashiers already know — nothing about fees is reimplemented
  // here. Null keeps the modal idle.
  const [feeStudent, setFeeStudent] = useState<any>(null)
  const feeTriggerRef = useRef<HTMLButtonElement>(null)

  // The colour crest, not the black one — the stationery this replaces uses
  // the full-colour logo, and the campus address and phone sit under the name.
  const branding = {
    schoolName: campusBrandName(currentBranch as any),
    campusName: currentBranch?.name || "",
    address: currentBranch?.address,
    phone: currentBranch?.contactNumber,
    logoUrl: absoluteCampusLogoUrl(currentBranch?.logoUrl),
  }

  const load = async () => {
    setLoading(true)
    try {
      const r = await listTransferCertificates({
        branchId: currentBranch?._id,
        sessionId: currentSession?._id,
        status: statusFilter || undefined,
        search: search.trim() || undefined,
        limit: 100,
      })
      setRows(r.data || [])
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Could not load the register")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [currentBranch?._id, currentSession?._id, statusFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  const lookUp = async (grOverride?: string) => {
    const value = (grOverride ?? gr).trim()
    if (!value) return
    setLooking(true)
    setEditing(null)
    try {
      const r = await findStudentForTC(value)
      setStudent(r.data)
      // Everything a person has to decide starts empty. Pre-filling "Good",
      // "Satisfactory" and today's date meant a certificate could be issued
      // without anyone having looked at those fields, and whatever was sitting
      // there became a permanent statement about the child.
      //
      // Class attending now and fee paid up to are exceptions: they are facts
      // read off the record, not judgements.
      setForm({
        classAttendingNow: r.data.className,
        classProgress: "",
        dateOfLeaving: "",
        reasonForLeaving: "",
        reasonForLeavingOther: "",
        progress: "",
        progressOther: "",
        conduct: "",
        conductOther: "",
        feePaidUpTo: r.data.feePaidUpTo,
        issueDate: "",
        // 2026-09-30: for recording a student who already left the school
        // before this system tracked TCs -- there's no live fee to collect,
        // so this skips the outstanding/transfer-fee checks entirely.
        // Starts unchecked; has to be a deliberate choice per certificate.
        feeWaived: false,
      })
    } catch (e: any) {
      setStudent(null)
      toast.error(e?.response?.data?.message || "No student found with that GR number")
    } finally {
      setLooking(false)
    }
  }

  // Deep link from Student Status's "TC issued" option — picking it there
  // opens this page pre-searched rather than changing status directly, so a
  // TC always goes through the real issuance flow (serial number, register
  // entry), never a status-only flip. Only meant to run once, off whatever
  // the URL carried on arrival.
  useEffect(() => {
    const grParam = searchParams.get("gr")
    if (grParam) {
      setGr(grParam)
      lookUp(grParam)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Re-read the student after a payment so the fee panel and the button
  // reflect what was just collected. Deliberately does NOT touch the form —
  // whoever is filling the certificate must not lose their typing to a refresh.
  const refreshFees = async (): Promise<TCStudent | null> => {
    if (!student) return null
    try {
      const r = await findStudentForTC(student.grNumber)
      setStudent(r.data)
      setForm((f) => ({ ...f, feePaidUpTo: r.data.feePaidUpTo }))
      return r.data
    } catch {
      // Leave the panel as it was; the API check still guards the issue itself.
      return null
    }
  }

  // The fee modal wants an enrolment, which is all the identity it uses to
  // build the bill. Same shape the Defaulters list hands it.
  const openCollect = () => {
    if (!student) return
    setFeeStudent({
      _id: student.enrollmentId || "",
      grNumber: student.grNumber,
      student: { _id: student.studentId, grNumber: student.grNumber },
      user: { firstName: student.firstName, lastName: student.lastName },
      class: { _id: student.classId || "", name: student.className },
      section: { _id: student.sectionId || "", name: student.sectionName },
      // The modal looks fee types up by class + system + session; without the
      // session it would price the child against the wrong year.
      session: { _id: student.sessionId || "" },
      name: `${student.firstName} ${student.lastName}`.trim(),
      admissionNumber: student.grNumber,
      status: "active",
    })
    feeTriggerRef.current?.click()
  }

  // "Pay & Issue" is one action, so when the payment settles the account we
  // carry straight on and issue rather than making them press a second time.
  // Read off the freshly fetched student, not state, which has not landed yet.
  const onFeeModalClose = async () => {
    setFeeStudent(null)
    const fresh = await refreshFees()
    if (!fresh) return
    const settled =
      (fresh.feeStatus?.outstanding || 0) === 0 &&
      !!fresh.transferFee?.configured &&
      !!fresh.transferFee.paid
    // They may have closed the modal without paying, or paid only part of it.
    if (settled) save(fresh)
  }

  const startEdit = (tc: TransferCertificate) => {
    setStudent(null)
    setEditing(tc)
    setForm({
      fatherName: tc.fatherName,
      placeOfBirth: tc.placeOfBirth,
      dateOfBirth: asInput(tc.dateOfBirth),
      dateOfAdmission: asInput(tc.dateOfAdmission),
      previousSchool: tc.previousSchool,
      classAttendingNow: tc.classAttendingNow,
      classProgress: tc.classProgress,
      dateOfLeaving: asInput(tc.dateOfLeaving),
      reasonForLeaving: tc.reasonForLeaving,
      reasonForLeavingOther: tc.reasonForLeavingOther,
      progress: tc.progress,
      progressOther: tc.progressOther,
      conduct: tc.conduct,
      conductOther: tc.conductOther,
      feePaidUpTo: tc.feePaidUpTo,
      issueDate: asInput(tc.issueDate),
    })
  }

  const closePanel = () => {
    setStudent(null)
    setEditing(null)
    setGr("")
  }

  const save = async (justPaid?: TCStudent) => {
    // After a payment the student in state has not re-rendered yet, so the
    // freshly read copy is passed in rather than waiting a tick for it.
    const target = justPaid || student
    setSaving(true)
    try {
      if (editing) {
        await updateTransferCertificate(editing._id, form)
        toast.success("Certificate updated")
      } else if (target) {
        const r = await issueTransferCertificate({ studentId: target.studentId, ...form })
        toast.success(r.message || "Certificate issued")
      }
      closePanel()
      load()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Could not save")
    } finally {
      setSaving(false)
    }
  }

  const cancel = async (tc: TransferCertificate) => {
    try {
      await cancelTransferCertificate(tc._id)
      toast.success(`${tc.serialNo} cancelled — the student is back on the rolls`)
      load()
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Could not cancel")
    }
  }

  const set = (k: string, v: any) => setForm((f) => ({ ...f, [k]: v }))

  const panelOpen = !!(student || editing)

  // Every judgement on the certificate has to be made deliberately. These are
  // permanent statements about a child that a parent may produce years later,
  // so an unfilled one must stop the certificate rather than print blank.
  const REQUIRED: [string, string][] = [
    ["classProgress", "Status of that class"],
    ["dateOfLeaving", "Date of leaving"],
    ["reasonForLeaving", "Reason for leaving"],
    ["progress", "Progress"],
    ["conduct", "Conduct"],
    ["issueDate", "Issue date"],
  ]
  const missing = REQUIRED.filter(([key]) => !String(form[key] || "").trim()).map(
    ([, label]) => label
  )
  // "Other" is only meaningful with the text that explains it.
  if (form.reasonForLeaving === "Other" && !String(form.reasonForLeavingOther || "").trim())
    missing.push("Reason (if Other)")
  if (form.progress === "Other" && !String(form.progressOther || "").trim())
    missing.push("Progress (if Other)")
  if (form.conduct === "Other" && !String(form.conductOther || "").trim())
    missing.push("Conduct (if Other)")

  // The account has to be settled before a certificate goes out. Enforced on
  // the API too — this only decides what the one button does and says.
  // feeWaived (historical record, already left) skips all of this — the API
  // enforces that skip too, this just keeps the button matching it.
  const tf = student?.transferFee
  const outstanding = student?.feeStatus?.outstanding || 0
  const feeWaived = !!form.feeWaived
  // Nobody has set a Transfer Fee up for this campus and system, so there is
  // nothing to charge and no certificate until somebody does. Eon is like this
  // today. This is the one case the screen cannot get past on its own.
  const noFeeConfigured = !!student && !editing && !feeWaived && !tf?.configured
  // Something is owed. The button collects it first instead of refusing.
  const needsPayment =
    !!student && !editing && !feeWaived && !noFeeConfigured && (outstanding > 0 || !tf?.paid)

  return (
    <>
    <div className="page-wrapper">
      <div className="content">
        <div className="mb-3">
          <h3 className="page-title mb-1">Transfer Certificates</h3>
          <p className="text-muted mb-0">
            Search a student by GR number to issue a certificate, or open one from the
            register below to edit or print it.
          </p>
        </div>

        {/* Issue: find the student first */}
        <div className="card">
          <div className="card-body">
            <label className="form-label">Search by GR number</label>
            <div className="d-flex gap-2 flex-wrap">
              <input
                className="form-control"
                style={{ maxWidth: 280 }}
                value={gr}
                placeholder="e.g. 135226"
                onChange={(e) => setGr(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && lookUp()}
              />
              <button className="btn btn-primary" onClick={() => lookUp()} disabled={looking || !gr.trim()}>
                {looking ? "Searching…" : "Search"}
              </button>
              {panelOpen && (
                <button className="btn btn-outline-secondary" onClick={closePanel}>
                  Close
                </button>
              )}
            </div>
          </div>
        </div>

        {panelOpen && (
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">
                {editing
                  ? `Edit ${editing.serialNo} — ${editing.firstName} ${editing.lastName}`
                  : `Issue certificate — ${student?.firstName} ${student?.lastName} (GR ${student?.grNumber})`}
              </h6>
            </div>
            <div className="card-body">
              {student && (
                <>
                  {student.status !== "admitted" && (
                    <div className="alert alert-light border text-dark py-2 small">
                      This student is already off the rolls. Issuing another certificate is
                      allowed, but check the register first.
                    </div>
                  )}
                  {student.existingCertificates.length > 0 && (
                    <div className="alert alert-light border text-dark py-2 small">
                      Already has {student.existingCertificates.length} certificate(s):{" "}
                      {student.existingCertificates.map((c) => c.serialNo).join(", ")}
                    </div>
                  )}
                  <div className="row g-3 mb-3">
                    {[
                      ["Campus", student.campusName],
                      ["Session", student.sessionTitle],
                      ["Class / Section", `${student.className} ${student.sectionName}`.trim()],
                      ["Father", student.fatherName],
                      ["Place of birth", student.placeOfBirth],
                      ["Date of birth", fmt(student.dateOfBirth)],
                    ].map(([label, value]) => (
                      <div className="col-md-4" key={label}>
                        <div className="text-muted small">{label}</div>
                        <div className="fw-medium">{value || "—"}</div>
                      </div>
                    ))}
                  </div>

                  {!editing && (
                    <div className="form-check mb-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="tc-fee-waived"
                        checked={feeWaived}
                        onChange={(e) => set("feeWaived", e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="tc-fee-waived">
                        Already left the school — no fee required (historical record)
                      </label>
                      <div className="form-text">
                        For a student who left before this system tracked TCs. Skips the
                        outstanding-fee and Transfer Fee checks below entirely — the
                        certificate is still fully recorded in the register either way.
                      </div>
                    </div>
                  )}

                  {/* What is owed, stated plainly. Collecting it is the Pay &
                      Issue button's job, not a second button up here. */}
                  <div className={`row g-3 mb-3 ${feeWaived ? "opacity-50" : ""}`}>
                    <div className="col-md-4">
                      <div className="text-muted small">Outstanding fees</div>
                      <div className={`fw-medium ${outstanding > 0 && !feeWaived ? "text-danger" : ""}`}>
                        {outstanding > 0
                          ? `Rs. ${outstanding.toLocaleString()}`
                          : "Nothing pending"}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-muted small">Transfer Fee</div>
                      <div className={`fw-medium ${(!tf?.configured || !tf.paid) && !feeWaived ? "text-danger" : ""}`}>
                        {!tf?.configured
                          ? "Not set up"
                          : `Rs. ${tf.amount.toLocaleString()} — ${tf.paid ? "paid" : "unpaid"}`}
                      </div>
                    </div>
                  </div>

                  {noFeeConfigured && (
                    <div className="alert alert-danger py-2 small">
                      Transfer Fee is not set up for {student.campusName} ·{" "}
                      {student.systemName}. Add it in Settings → Fee Types before a
                      certificate can be issued here.
                    </div>
                  )}
                </>
              )}

              <div className="row g-3">
                {editing && (
                  <>
                    <div className="col-md-4">
                      <label className="form-label">Father's name</label>
                      <input className="form-control" value={form.fatherName || ""} onChange={(e) => set("fatherName", e.target.value)} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Place of birth</label>
                      <input className="form-control" value={form.placeOfBirth || ""} onChange={(e) => set("placeOfBirth", e.target.value)} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Date of birth</label>
                      <input type="date" className="form-control" value={form.dateOfBirth || ""} onChange={(e) => set("dateOfBirth", e.target.value)} />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Previous school attended</label>
                      <input className="form-control" value={form.previousSchool || ""} onChange={(e) => set("previousSchool", e.target.value)} />
                    </div>
                  </>
                )}

                <div className="col-md-4">
                  <label className="form-label">Class attending now</label>
                  <input className="form-control" value={form.classAttendingNow || ""} onChange={(e) => set("classAttendingNow", e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Status of that class</label>
                  <select className="form-select" value={form.classProgress || ""} onChange={(e) => set("classProgress", e.target.value)}>
                    <option value="">— Select —</option>
                    {PROGRESS_STATES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Fee paid up to</label>
                  <input className="form-control" value={form.feePaidUpTo || ""} placeholder="e.g. Jul(25-26)" onChange={(e) => set("feePaidUpTo", e.target.value)} />
                  {/* An empty box has two very different causes — never
                      invoiced, or invoiced and unpaid — and whoever signs the
                      certificate should be told which rather than guessing. */}
                  <small className={student?.feeStatus?.outstanding ? "text-danger" : "text-muted"}>
                    {student?.feeStatus?.label
                      ? `${student.feeStatus.label}. Worked out from paid invoices — correct it if needed.`
                      : "Worked out from paid invoices — correct it if needed."}
                  </small>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Date of leaving</label>
                  <input type="date" className="form-control" value={form.dateOfLeaving || ""} onChange={(e) => set("dateOfLeaving", e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Reason for leaving</label>
                  <select className="form-select" value={form.reasonForLeaving || ""} onChange={(e) => set("reasonForLeaving", e.target.value)}>
                    <option value="">— Select —</option>
                    {REASONS.map((r) => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Reason (if Other)</label>
                  <input className="form-control" disabled={form.reasonForLeaving !== "Other"} value={form.reasonForLeavingOther || ""} onChange={(e) => set("reasonForLeavingOther", e.target.value)} />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Progress</label>
                  <select className="form-select" value={form.progress || ""} onChange={(e) => set("progress", e.target.value)}>
                    <option value="">— Select —</option>
                    {GRADES.map((g) => <option key={g}>{g}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Progress (if Other)</label>
                  <input className="form-control" disabled={form.progress !== "Other"} value={form.progressOther || ""} onChange={(e) => set("progressOther", e.target.value)} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Conduct</label>
                  <select className="form-select" value={form.conduct || ""} onChange={(e) => set("conduct", e.target.value)}>
                    <option value="">— Select —</option>
                    {GRADES.map((g) => <option key={g}>{g}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Conduct (if Other)</label>
                  <input className="form-control" disabled={form.conduct !== "Other"} value={form.conductOther || ""} onChange={(e) => set("conductOther", e.target.value)} />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Issue date</label>
                  <input type="date" className="form-control" value={form.issueDate || ""} onChange={(e) => set("issueDate", e.target.value)} />
                </div>
              </div>

              {!editing && (
                <div className="alert alert-light border text-dark py-2 small mt-3 mb-0">
                  Issuing this certificate sets the student to <strong>TC</strong>, which
                  removes them from class lists, RMS and the head count. All their records
                  are kept, and it can be reversed by cancelling the certificate.
                </div>
              )}
            </div>
            <div className="card-footer d-flex justify-content-end align-items-center gap-3">
              {missing.length > 0 && (
                <small className="text-danger me-auto">
                  Still to fill: {missing.join(", ")}
                </small>
              )}
              <button className="btn btn-secondary" onClick={closePanel}>Cancel</button>
              {/* Bootstrap opens the fee modal off an attribute, and the one
                  button only sometimes needs it, so the attribute lives on a
                  hidden trigger this clicks rather than on the button itself. */}
              <button
                ref={feeTriggerRef}
                type="button"
                className="d-none"
                data-bs-toggle="modal"
                data-bs-target="#create_fee_modal"
              />
              <button
                className="btn btn-primary"
                onClick={() => (needsPayment ? openCollect() : save())}
                disabled={saving || missing.length > 0 || noFeeConfigured}
              >
                {saving
                  ? "Saving…"
                  : editing
                    ? "Update"
                    : needsPayment
                      ? "Pay & Issue"
                      : "Issue certificate"}
              </button>
            </div>
          </div>
        )}

        {/* Register */}
        <div className="card">
          <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
            <h6 className="mb-0">Register — students who took a TC</h6>
            <div className="d-flex gap-2 flex-wrap">
              <select className="form-select form-select-sm" style={{ width: 150 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All</option>
                <option value="issued">Issued</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <input
                className="form-control form-control-sm"
                style={{ width: 220 }}
                placeholder="Serial, GR or name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
              />
              <button className="btn btn-sm btn-outline-secondary" onClick={load}>Search</button>
            </div>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center text-muted py-4">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="text-center text-muted py-4">No certificates yet for this campus.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Serial</th><th>GR</th><th>Name</th><th>Class</th>
                      <th>Left on</th><th>Reason</th><th>Issued by</th><th>Status</th><th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((tc) => (
                      <tr key={tc._id}>
                        <td className="fw-medium">{tc.serialNo}</td>
                        <td>{tc.grNumber}</td>
                        <td>{`${tc.firstName} ${tc.lastName}`.trim()}</td>
                        <td>{`${tc.className} ${tc.sectionName}`.trim()}</td>
                        <td>{fmt(tc.dateOfLeaving)}</td>
                        <td>{tc.reasonForLeaving === "Other" ? tc.reasonForLeavingOther : tc.reasonForLeaving}</td>
                        <td>{tc.issuedByName || "—"}</td>
                        <td>
                          <span className={`badge ${tc.status === "issued" ? "badge-soft-success" : "badge-soft-danger"}`}>
                            {tc.status === "issued" ? "Issued" : "Cancelled"}
                          </span>
                        </td>
                        <td className="text-nowrap">
                          <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => startEdit(tc)}>Edit</button>
                          <button
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => {
                              if (!printTransferCertificate(tc, branding)) {
                                toast.error("Popup blocked — allow popups and try again")
                              }
                            }}
                          >
                            Print
                          </button>
                          {tc.status === "issued" && (
                            <button className="btn btn-sm btn-outline-danger" onClick={() => cancel(tc)}>Cancel</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    {/* Always mounted, idle until a student is handed to it — the same modal
        Fees Collection opens, so the payment and its receipt are identical. */}
    <CollectFeesModal
      onClose={onFeeModalClose}
      studentData={feeStudent}
      selectedSystem={student?.systemId || undefined}
      // The modal will not load its fees at all without this. It gates the
      // load and is not read beyond that, so the tuition group — the one whose
      // due months auto-fill — is what it wants here.
      feeGroup={TUITION_FEE_GROUP as string}
    />
    </>
  )
}

export default TransferCertificatePage
